import { applyUciMove, gameFromSan, uciLineToSan } from "@onchainreps/chess";

export type DrillKind = "opening" | "puzzle" | "mistake";

export interface DrillItem {
  id: string;
  kind: DrillKind;
  fen: string;
  expectedUci: string[];
  expectedSan: string[];
  title: string;
  subtitle: string;
  tags: string[];
  continuation: string[];
}

export interface OpeningCourse {
  slug: string;
  eco: string;
  name: string;
  family: string;
  color: "white" | "black";
  moves: string[];
}

export interface CourseCatalog {
  generatedAt: string;
  source: string;
  license: string;
  families: string[];
  openings: OpeningCourse[];
}

export function openingDrills(course: OpeningCourse): DrillItem[] {
  let game;
  try {
    game = gameFromSan(course.moves, { Opening: course.name, ECO: course.eco });
  } catch {
    return [];
  }

  const drills: DrillItem[] = [];

  for (const move of game.moves) {
    if (move.color !== course.color) continue;
    drills.push({
      id: `opening:${course.slug}:${move.ply}`,
      kind: "opening",
      fen: move.fenBefore,
      expectedUci: [move.uci],
      expectedSan: [move.san],
      title: course.name,
      subtitle: `${course.color === "white" ? "White" : "Black"} to play, move ${move.moveNumber}`,
      tags: [course.family, course.eco],
      continuation: game.moves
        .filter((candidate) => candidate.ply > move.ply)
        .slice(0, 4)
        .map((candidate) => candidate.san),
    });
  }

  return drills;
}

export function puzzleDrill(
  puzzle: { id: string; fen: string; moves: string; rating: number; themes: string },
): DrillItem {
  const solution = puzzle.moves.split(" ").filter(Boolean);
  const first = solution[0] ?? "";
  const after = first ? applyUciMove(puzzle.fen, first) : null;
  const continuation = after?.legal ? uciLineToSan(after.fen, solution.slice(1)) : [];
  return {
    id: `puzzle:${puzzle.id}`,
    kind: "puzzle",
    fen: puzzle.fen,
    expectedUci: [first],
    expectedSan: [],
    title: "Find the move",
    subtitle: `${puzzle.rating} rated`,
    tags: puzzle.themes ? puzzle.themes.split(" ").filter(Boolean) : [],
    continuation,
  };
}

export function mistakeDrill(
  fen: string,
  expectedUci: string,
  label: string,
  sourcePly: number,
): DrillItem {
  return {
    id: `mistake:${sourcePly}:${fen}`,
    kind: "mistake",
    fen,
    expectedUci: [expectedUci],
    expectedSan: [],
    title: "Your position, your mistake",
    subtitle: label,
    tags: ["mistake", "personal"],
    continuation: [],
  };
}

export function isCorrectMove(drill: DrillItem, uci: string): boolean {
  return drill.expectedUci.some((candidate) => {
    if (candidate.length === uci.length) return candidate === uci;
    return candidate.slice(0, 4) === uci.slice(0, 4);
  });
}
